/**
 * Graph App Process
 * 
 * Experimental mini-app demonstrating that most applications (99%) are graphs.
 * Modular architecture with reusable components.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { GraphEngine } from './graph-engine.js';
import { getViewModeConfig } from './view-modes.js';
import type { ViewMode, GraphNode, GraphEdge, StackInteractionMode } from './types.js';
import { nanoid } from 'nanoid';
import { renderGraphView } from './graph-renderer.js';
import { 
  designTokens, 
  createHeading,
  createButton,
  createContainer,
  createSelect
} from '../../core/design-system.js';
import { getPriorityIndicatorColor, getEventBarColor } from '../../core/priority-colors.js';
import { DAY_NAMES, isToday } from '../../core/date-utils.js';
import { renderEventBars } from '../../core/event-bar-renderer.js';
import { renderCalendarCell, renderCalendarDayNames } from '../../core/calendar-cell-renderer.js';
import { renderListItems } from '../../core/list-item-renderer.js';
import type { ListItemConfig } from '../../core/list-item-renderer.js';

interface GraphAppState {
  viewMode: ViewMode;
  containerCount: number;
  contentNodes: GraphNode[];
  viewOptions?: {
    carouselItemsVisible?: number;
    gridUniform?: boolean;
    gridRows?: number;
    gridColumns?: number;
    listEditable?: boolean;
    calendarViewType?: 'month' | 'week';
    stackInteractionMode?: StackInteractionMode;
    stackOnAction?: (nodeId: string, action: 'left' | 'right') => void;
  };
}

export class GraphAppProcess extends BaseProcess {
  kind: string = 'graph-app';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'demo' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private graphEngine: GraphEngine = new GraphEngine();
  private state: GraphAppState = {
    viewMode: 'list',
    containerCount: 7,
    contentNodes: [],
    viewOptions: {
      carouselItemsVisible: 1,
      gridUniform: true,
      listEditable: true,
      calendarViewType: 'week',
      stackInteractionMode: 'browse'
    }
  };

  private graphAppView: GraphAppView | null = null;

  constructor(id: string) {
    super(id);
    this.initializeSampleData();
  }

  /**
   * Initialize with rich sample content nodes
   * Demonstrates different data types that work across view modes
   */
  private initializeSampleData(): void {
    const sampleData = [
      { title: 'Team Meeting', description: 'Weekly sync with design team', date: 0, time: '10:00', category: 'work' },
      { title: 'Gym Session', description: 'Morning workout', date: 0, time: '07:00', category: 'fitness', completed: true },
      { title: 'Project Review', description: 'Review Q1 deliverables', date: 1, time: '14:00', category: 'work' },
      { title: 'Dinner Plans', description: 'Restaurant reservation', date: 1, time: '19:00', category: 'personal' },
      { title: 'Code Review', description: 'Review PR #1234', date: 2, time: '11:00', category: 'work', completed: true },
      { title: 'Photo Shoot', description: 'Product photography', date: 2, time: '15:00', category: 'work' },
      { title: 'Doctor Appointment', description: 'Annual checkup', date: 3, time: '09:00', category: 'health' },
      { title: 'Book Club', description: 'Discuss "The Design of Everyday Things"', date: 3, time: '18:00', category: 'personal' },
      { title: 'Client Call', description: 'Discuss new requirements', date: 4, time: '13:00', category: 'work' },
      { title: 'Weekend Trip', description: 'Mountain hiking', date: 5, category: 'personal' },
      { title: 'Budget Planning', description: 'Review monthly expenses', date: 6, time: '16:00', category: 'finance' },
      { title: 'Learn React', description: 'Complete advanced hooks tutorial', category: 'learning', completed: false }
    ];

    sampleData.forEach((item, i) => {
      const node: GraphNode = {
        id: `content-${nanoid()}`,
        type: 'content',
        data: {
          title: item.title,
          description: item.description,
          category: item.category,
          ...(item.date !== undefined ? {
            date: new Date(Date.now() + item.date * 24 * 60 * 60 * 1000).toISOString(),
            ...(item.time ? { time: item.time } : {})
          } : {}),
          ...(item.completed !== undefined ? { completed: item.completed } : {}),
          // Add images for some items
          ...(i % 3 === 0 ? {
            imageUrl: `https://picsum.photos/300/200?random=${i}`,
            thumbnail: `https://picsum.photos/150/150?random=${i}`
          } : {})
        },
        metadata: {
          label: item.title
        }
      };
      
      this.state.contentNodes.push(node);
    });
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[GraphAppProcess] Starting graph app process ${this.id}`);
    
    // Listen for view mode changes
    bus.on('graph-app.view-mode.change', (payload: unknown) => {
      const data = payload as { viewMode: ViewMode; containerCount?: number };
      this.setViewMode(data.viewMode, data.containerCount);
    });
    
    // Auto-show view on start
    this.showGraphAppView(ctx);
    
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  private showGraphAppView(ctx: ProcessContext): void {
    if (this.graphAppView) {
      return; // Already showing
    }
    
    const view = new GraphAppView(
      this.id,
      `${this.id}-main`,
      this.state,
      this.graphEngine,
      this
    );
    const viewInstance = view.toViewInstance();
    this.graphAppView = view;
    
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
  }

  setViewMode(viewMode: ViewMode, containerCount?: number): void {
    this.state.viewMode = viewMode;
    if (containerCount !== undefined) {
      this.state.containerCount = containerCount;
    }
    
    // Rebuild graph with new view mode
    this.rebuildGraph();
    
    if (this.graphAppView) {
      this.graphAppView.updateState(this.state);
    }
  }

  /**
   * Rebuild graph based on current view mode
   */
  private rebuildGraph(): void {
    this.graphEngine.clear();
    
    const config = getViewModeConfig(
      this.state.viewMode,
      this.state.containerCount
    );
    
    // Generate container nodes if strategy exists
    if (config.containerStrategy) {
      const containers = config.containerStrategy.generateContainers(
        config.containerCount || 7,
        config.containerConfig || {}
      );
      
      containers.forEach(container => {
        this.graphEngine.addNode(container);
      });
      
      // Map content nodes to containers
      this.state.contentNodes.forEach((contentNode, index) => {
        this.graphEngine.addNode(contentNode);
        
        // Create membership edges
        if (containers.length > 0) {
          const containerIndex = index % containers.length;
          const container = containers[containerIndex];
          
          this.graphEngine.addEdge({
            id: `edge-${nanoid()}`,
            from: contentNode.id,
            to: container.id,
            type: 'membership'
          });
        }
      });
    } else {
      // No containers - just add content nodes
      this.state.contentNodes.forEach(node => {
        this.graphEngine.addNode(node);
      });
    }
    
    // For graph view, create relationship edges between content nodes
    // (only after all nodes are added)
    if (this.state.viewMode === 'graph') {
      this.createRelationshipEdges();
    }
  }

  /**
   * Create relationship edges between content nodes for graph view
   * Connects nodes based on category, date proximity, or other relationships
   */
  private createRelationshipEdges(): void {
    const nodes = this.state.contentNodes;
    
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const nodeA = nodes[i];
        const nodeB = nodes[j];
        let shouldConnect = false;
        let edgeType: GraphEdge['type'] = 'dependency';
        
        // Connect nodes with same category
        if (nodeA.data.category === nodeB.data.category) {
          shouldConnect = true;
          edgeType = 'dependency';
        }
        
        // Connect nodes with close dates (within 2 days)
        if (nodeA.data.date && nodeB.data.date) {
          const dateA = new Date(nodeA.data.date as string);
          const dateB = new Date(nodeB.data.date as string);
          const daysDiff = Math.abs((dateA.getTime() - dateB.getTime()) / (1000 * 60 * 60 * 24));
          if (daysDiff <= 2) {
            shouldConnect = true;
            edgeType = 'temporal';
          }
        }
        
        // Randomly connect some nodes (30% chance) for visual interest
        if (!shouldConnect && Math.random() < 0.3) {
          shouldConnect = true;
          edgeType = 'dependency';
        }
        
        if (shouldConnect) {
          this.graphEngine.addEdge({
            id: `edge-${nanoid()}`,
            from: nodeA.id,
            to: nodeB.id,
            type: edgeType
          });
        }
      }
    }
  }

  getState(): GraphAppState {
    return { ...this.state };
  }

  getGraphEngine(): GraphEngine {
    return this.graphEngine;
  }
}

/**
 * Graph App View
 * 
 * Renders the graph based on current view mode.
 */
class GraphAppView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private state: GraphAppState,
    private graphEngine: GraphEngine,
    private process: GraphAppProcess
  ) {
    super(processId, viewId, {
      showDismissButton: true,
      resizable: true,
      sizes: [1, 3] // [1] = full+standard (default), [3] = full+double
    });
    
    // Initial graph build
    this.process['rebuildGraph']();
  }

  /**
   * Setup handlers to prevent window dragging when interacting with visualization content
   */
  protected render(): void {
    super.render();
    
    if (!this.element) return;
    
    // Prevent window dragging when clicking inside visualization area
    // This allows users to interact with graph content without accidentally dragging the window
    const visualizationArea = this.element.querySelector('.graph-app-visualization');
    if (visualizationArea) {
      // Stop mousedown events from propagating to the view element
      // This prevents the drag handler from being triggered
      visualizationArea.addEventListener('mousedown', (e) => {
        e.stopPropagation();
      }, true); // Use capture phase to catch early
      
      // Also prevent dragging on interactive elements within visualization
      const interactiveElements = visualizationArea.querySelectorAll(
        '.graph-node, svg, .graph-app-item, .graph-app-day, .graph-app-cell, .graph-app-slide, .graph-app-card, input, select, button'
      );
      interactiveElements.forEach(el => {
        el.addEventListener('mousedown', (e) => {
          e.stopPropagation();
        }, true);
      });
    }
    
    // Setup stack swipe functionality
    const stack = this.element.querySelector('.graph-app-stack') as HTMLElement;
    if (stack) {
      this.setupStackSwipe(stack);
    }
    
    // View mode selector
    const viewModeSelect = this.element.querySelector('.graph-app-view-mode-select') as HTMLSelectElement;
    if (viewModeSelect) {
      viewModeSelect.addEventListener('change', (e) => {
        const viewMode = (e.target as HTMLSelectElement).value as ViewMode;
        this.process.setViewMode(viewMode);
      });
    }
    
    // Container count input
    const containerCountInput = this.element.querySelector('.graph-app-container-count-input') as HTMLInputElement;
    if (containerCountInput) {
      containerCountInput.addEventListener('change', (e) => {
        const count = parseInt((e.target as HTMLInputElement).value, 10);
        if (!isNaN(count) && count > 0) {
          this.process.setViewMode(this.state.viewMode, count);
        }
      });
    }
  }

  protected getContentHTML(): string {
    const heading = createHeading(2, 'Graph App');
    
    // View mode selector
    const viewModeSelect = createSelect(
      [
        { value: 'list', label: 'List' },
        { value: 'calendar', label: 'Calendar (Week)' },
        { value: 'grid', label: 'Grid' },
        { value: 'carousel', label: 'Carousel' },
        { value: 'stack', label: 'Stack (Flashcards)' },
        { value: 'graph', label: 'Graph' }
      ],
      this.state.viewMode,
      {
        className: 'graph-app-view-mode-select',
        name: 'viewMode'
      }
    );
    
    // Container count input (for calendar/grid)
    const containerCountInput = (this.state.viewMode === 'calendar' || this.state.viewMode === 'grid') ? `
      <div style="margin-bottom: ${designTokens.padding.md};">
        <label style="
          display: block;
          margin-bottom: ${designTokens.padding.xs};
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColor};
        ">Container Count</label>
        <input 
          type="number" 
          class="graph-app-container-count-input"
          value="${this.state.containerCount}"
          min="1"
          max="100"
          style="
            width: 100%;
            padding: ${designTokens.padding.sm};
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid ${designTokens.borderColor};
            border-radius: ${designTokens.radius.sm};
            color: ${designTokens.contentColor};
          "
        />
      </div>
    ` : '';
    
    // Graph visualization
    const graphVisualization = this.renderGraph();
    
    return createContainer(
      heading +
      `<div style="margin-bottom: ${designTokens.padding.md};">
        <label style="
          display: block;
          margin-bottom: ${designTokens.padding.xs};
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColor};
        ">View Mode</label>
        ${viewModeSelect}
      </div>` +
      containerCountInput +
      `<div class="graph-app-visualization" style="
        margin-top: ${designTokens.padding.lg};
        min-height: 400px;
        border: 1px solid ${designTokens.borderColor};
        border-radius: ${designTokens.radius.md};
        padding: ${designTokens.padding.md};
        overflow: auto;
        transition: opacity 0.3s ease, transform 0.3s ease;
      ">
        ${graphVisualization}
      </div>`,
      { padding: 'lg', flexDirection: 'column' }
    );
  }

  /**
   * Render graph based on current view mode
   */
  private renderGraph(): string {
    const config = getViewModeConfig(
      this.state.viewMode,
      this.state.containerCount
    );
    
    const containerNodes = this.graphEngine.getNodesByType('container');
    const contentNodes = this.graphEngine.getNodesByType('content');
    
    if (this.state.viewMode === 'graph') {
      // Graph view - force-directed layout
      return this.renderGraphView(contentNodes);
    } else if (this.state.viewMode === 'stack') {
      // Stack view - z-ordered cards
      return this.renderStackView(contentNodes);
    } else if (this.state.viewMode === 'carousel') {
      // Carousel view - horizontal scroll
      return this.renderCarouselView(containerNodes, contentNodes);
    } else if (this.state.viewMode === 'calendar') {
      // Calendar view - grid of days
      return this.renderCalendarView(containerNodes, contentNodes);
    } else if (this.state.viewMode === 'grid') {
      // Grid view - grid of cells
      return this.renderGridView(containerNodes, contentNodes);
    } else {
      // List view - vertical list
      return this.renderListView(contentNodes);
    }
  }

  /**
   * Get node data with graceful fallbacks
   */
  private getNodeData(node: GraphNode, field: string, fallback: string = ''): string {
    // Try metadata first, then data, then fallback
    return (node.metadata?.[field] as string) || 
           (node.data[field] as string) || 
           fallback;
  }

  /**
   * Check if node has a specific field
   */
  private hasNodeData(node: GraphNode, field: string): boolean {
    return !!(node.metadata?.[field] || node.data[field]);
  }

  private renderListView(contentNodes: GraphNode[]): string {
    const isEditable = this.state.viewOptions?.listEditable !== false; // Default to true
    const listItems: ListItemConfig[] = contentNodes.map(node => {
      const title = this.getNodeData(node, 'title') || 
                    this.getNodeData(node, 'label') || 
                    'Item';
      const description = this.getNodeData(node, 'description', '');
      const completed = node.data.completed as boolean;
      const hasCompleted = this.hasNodeData(node, 'completed');
      const priority = node.data.priority as 'low' | 'medium' | 'high' | undefined;
      
      return {
        id: node.id,
        title,
        description: description || undefined,
        completed,
        priority,
        showCheckbox: isEditable && hasCompleted,
        showPriority: !!priority,
        showDelete: false
      };
    });
    
    return `
      <ul style="
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: ${designTokens.spacing.listGap};
      ">${renderListItems(listItems)}</ul>
    `;
  }

  private renderCalendarView(containerNodes: GraphNode[], contentNodes: GraphNode[]): string {
    const viewType = this.state.viewOptions?.calendarViewType || 'week';
    
    if (viewType === 'week') {
      // Week view: 7 full-width rows, one per day
      const days = containerNodes.slice(0, 7).map((container, index) => {
        const contents = this.graphEngine.getContentNodesForContainer(container.id);
        const containerDate = container.data.date ? new Date(container.data.date as string) : null;
        const date = containerDate || new Date(Date.now() + index * 24 * 60 * 60 * 1000);
        const dayName = DAY_NAMES[date.getDay()];
        const dayNumber = date.getDate();
        const isTodayDate = isToday(date);
        
        const events = contents.map(node => ({
          id: node.id,
          title: this.getNodeData(node, 'title') || 
                 this.getNodeData(node, 'eventTitle') ||
                 this.getNodeData(node, 'label') || 
                 'Event',
          priority: node.data.priority as 'high' | 'medium' | 'low' | undefined,
          time: this.getNodeData(node, 'time', '')
        }));
        
        return `
          <div class="calendar-week-day" style="
            display: flex;
            flex-direction: column;
            padding: ${designTokens.padding.md};
            border-bottom: 1px solid ${designTokens.borderColor};
            background: ${isTodayDate ? 'rgba(255, 255, 255, 0.05)' : 'transparent'};
          ">
            <div style="
              display: flex;
              align-items: center;
              gap: ${designTokens.padding.md};
              margin-bottom: ${designTokens.padding.sm};
            ">
              <div style="
                font-weight: ${isTodayDate ? 'bold' : '600'};
                font-size: ${designTokens.fontSize.sm};
                color: ${designTokens.contentColor};
                min-width: 40px;
              ">${dayName}</div>
              <div style="
                font-size: ${designTokens.fontSize.md};
                font-weight: ${isTodayDate ? 'bold' : 'normal'};
                color: ${designTokens.contentColor};
              ">${dayNumber}</div>
            </div>
            <div style="
              display: flex;
              flex-direction: column;
              gap: ${designTokens.padding.xs};
            ">
              ${events.length === 0 ? `
                <div style="
                  font-size: ${designTokens.fontSize.xs};
                  color: ${designTokens.contentColorMuted};
                  font-style: italic;
                ">No events</div>
              ` : events.map(event => `
                <div style="
                  display: flex;
                  align-items: center;
                  gap: ${designTokens.padding.sm};
                  padding: ${designTokens.padding.xs};
                  background: rgba(255, 255, 255, 0.05);
                  border-radius: ${designTokens.radius.sm};
                ">
                  ${event.time ? `
                    <div style="
                      font-size: ${designTokens.fontSize.xs};
                      color: ${designTokens.contentColorMuted};
                      min-width: 50px;
                    ">${this.escapeHtml(event.time)}</div>
                  ` : ''}
                  <div style="
                    flex: 1;
                    font-size: ${designTokens.fontSize.sm};
                    color: ${designTokens.contentColor};
                  ">${this.escapeHtml(event.title)}</div>
                  ${event.priority ? `
                    <div style="
                      width: 8px;
                      height: 8px;
                      border-radius: 50%;
                      background: ${getEventBarColor(event.priority)};
                    "></div>
                  ` : ''}
                </div>
              `).join('')}
            </div>
          </div>
        `;
      }).join('');
      
      return `
        <div style="
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow-y: auto;
        ">${days}</div>
      `;
    } else {
      // Month view: grid layout (original)
      const dayNamesHTML = containerNodes.length === 7 ? renderCalendarDayNames() : '';
      
      const days = containerNodes.map((container, index) => {
        const contents = this.graphEngine.getContentNodesForContainer(container.id);
        const containerDate = container.data.date ? new Date(container.data.date as string) : null;
        const date = containerDate || new Date();
        const dayNumber = containerDate ? containerDate.getDate() : (index + 1);
        
        const events = contents.map(node => ({
          id: node.id,
          title: this.getNodeData(node, 'title') || 
                 this.getNodeData(node, 'eventTitle') ||
                 this.getNodeData(node, 'label') || 
                 'Event',
          priority: node.data.priority as 'high' | 'medium' | 'low' | undefined
        }));
        
        return renderCalendarCell({
          date,
          dayNumber,
          isCurrentMonth: true,
          events,
          maxVisibleEvents: 3
        });
      }).join('');
      
      return `
        <style>
          .graph-app-calendar-grid {
            display: grid;
            grid-template-columns: repeat(${Math.min(containerNodes.length, 7)}, 1fr);
            gap: ${designTokens.spacing.gridGap};
            flex: 1;
            min-height: 0;
          }
          .calendar-day:hover {
            background: rgba(255, 255, 255, 0.1) !important;
          }
          .event-bar:hover {
            opacity: 0.8;
          }
        </style>
        ${dayNamesHTML}
        <div class="graph-app-calendar-grid">${days}</div>
      `;
    }
  }

  private renderGridView(containerNodes: GraphNode[], contentNodes: GraphNode[]): string {
    const isUniform = this.state.viewOptions?.gridUniform !== false; // Default to true
    const rows = this.state.viewOptions?.gridRows;
    const columns = this.state.viewOptions?.gridColumns;
    
    // Calculate optimal grid dimensions if not provided
    const totalItems = containerNodes.length;
    let calculatedRows = rows;
    let calculatedColumns = columns;
    
    if (!rows && !columns) {
      // Auto-calculate: aim for roughly square-ish grid
      calculatedColumns = Math.ceil(Math.sqrt(totalItems));
      calculatedRows = Math.ceil(totalItems / calculatedColumns);
    } else if (rows && !columns) {
      calculatedColumns = Math.ceil(totalItems / rows);
    } else if (columns && !rows) {
      calculatedRows = Math.ceil(totalItems / columns);
    }
    
    const cells = containerNodes.map(container => {
      const contents = this.graphEngine.getContentNodesForContainer(container.id);
      const contentHTML = contents.map(node => {
        const imageUrl = this.getNodeData(node, 'image') || 
                         this.getNodeData(node, 'imageUrl') ||
                         this.getNodeData(node, 'thumbnail');
        const title = this.getNodeData(node, 'title') || 
                      this.getNodeData(node, 'label') || 
                      'Item';
        const hasImage = this.hasNodeData(node, 'image') || 
                         this.hasNodeData(node, 'imageUrl') ||
                         this.hasNodeData(node, 'thumbnail');
        
        if (hasImage && imageUrl) {
          return `
            <div style="
              width: 100%;
              ${isUniform ? 'aspect-ratio: 1;' : 'min-height: 150px;'}
              background: rgba(0, 0, 0, 0.1);
              border-radius: ${designTokens.radius.sm};
              overflow: hidden;
              ${isUniform ? '' : 'display: flex; align-items: center; justify-content: center;'}
            ">
              <img 
                src="${this.escapeHtml(imageUrl)}" 
                alt="${this.escapeHtml(title)}"
                style="width: 100%; height: 100%; object-fit: cover;"
                onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=\\'padding: 4px; color: rgba(255,255,255,0.5); font-size: 10px;\\'>${this.escapeHtml(title)}</div>';"
              />
            </div>
          `;
        } else {
          return `
            <div style="
              padding: ${designTokens.padding.sm};
              background: rgba(255, 255, 255, 0.05);
              border-radius: ${designTokens.radius.sm};
              font-size: ${designTokens.fontSize.sm};
              color: ${designTokens.contentColor};
              ${isUniform ? 'min-height: 100px; display: flex; align-items: center;' : ''}
            ">
              ${this.escapeHtml(title)}
            </div>
          `;
        }
      }).join('');
      
      return `
        <div class="graph-app-cell" style="
          ${isUniform ? '' : 'display: flex; flex-direction: column;'}
          padding: ${designTokens.spacing.cellPadding};
          background: transparent;
        ">
          ${contentHTML || ''}
        </div>
      `;
    }).join('');
    
    if (isUniform) {
      // Uniform grid: all cells same size
      return `
        <div style="
          display: grid;
          grid-template-columns: repeat(${calculatedColumns}, 1fr);
          grid-template-rows: repeat(${calculatedRows}, 1fr);
          gap: ${designTokens.spacing.gridGap};
          height: 100%;
          width: 100%;
        ">${cells}</div>
      `;
    } else {
      // Non-uniform: flexbox with wrapping, size by content
      return `
        <div style="
          display: flex;
          flex-wrap: wrap;
          gap: ${designTokens.spacing.gridGap};
          align-content: flex-start;
          height: 100%;
          width: 100%;
        ">${cells}</div>
      `;
    }
  }

  private renderCarouselView(containerNodes: GraphNode[], contentNodes: GraphNode[]): string {
    const itemsVisible = this.state.viewOptions?.carouselItemsVisible || 1;
    const slideWidth = `${100 / itemsVisible}%`;
    
    const slides = containerNodes.map((container, index) => {
      const contents = this.graphEngine.getContentNodesForContainer(container.id);
      const contentHTML = contents.map(node => {
        const imageUrl = this.getNodeData(node, 'image') || 
                         this.getNodeData(node, 'imageUrl') ||
                         this.getNodeData(node, 'thumbnail');
        const title = this.getNodeData(node, 'title') || 
                      this.getNodeData(node, 'label') || 
                      'Item';
        const description = this.getNodeData(node, 'description', '');
        const hasImage = this.hasNodeData(node, 'image') || 
                         this.hasNodeData(node, 'imageUrl') ||
                         this.hasNodeData(node, 'thumbnail');
        
        if (hasImage && imageUrl) {
          return `
            <div style="
              width: 100%;
              aspect-ratio: 16/9;
              background: rgba(0, 0, 0, 0.1);
              border-radius: ${designTokens.radius.sm};
              overflow: hidden;
              margin-bottom: ${designTokens.padding.xs};
              position: relative;
            ">
              <img 
                src="${this.escapeHtml(imageUrl)}" 
                alt="${this.escapeHtml(title)}"
                style="width: 100%; height: 100%; object-fit: cover;"
                onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=\\'padding: 8px;\\'><div style=\\'font-weight: 600; color: ${designTokens.contentColor};\\'>${this.escapeHtml(title)}</div></div>';"
              />
              ${title ? `
                <div style="
                  position: absolute;
                  bottom: 0;
                  left: 0;
                  right: 0;
                  padding: ${designTokens.padding.sm};
                  background: linear-gradient(to top, rgba(0,0,0,0.6), transparent);
                  color: white;
                  font-size: ${designTokens.fontSize.sm};
                  font-weight: 500;
                ">
                  ${this.escapeHtml(title)}
                </div>
              ` : ''}
            </div>
          `;
        } else {
          return `
            <div style="
              padding: ${designTokens.padding.md};
              background: rgba(255, 255, 255, 0.05);
              border-radius: ${designTokens.radius.sm};
            ">
              <div style="font-weight: 500; color: ${designTokens.contentColor}; font-size: ${designTokens.fontSize.sm};">
                ${this.escapeHtml(title)}
              </div>
              ${description ? `
                <div style="font-size: ${designTokens.fontSize.xs}; color: ${designTokens.contentColorMuted}; margin-top: ${designTokens.padding.xs};">
                  ${this.escapeHtml(description)}
                </div>
              ` : ''}
            </div>
          `;
        }
      }).join('');
      
      return `
        <div 
          class="graph-app-slide" 
          data-slide-index="${index}"
          style="
            width: ${slideWidth};
            flex-shrink: 0;
            scroll-snap-align: start;
            padding: 0 ${designTokens.padding.xs};
            box-sizing: border-box;
          ">
          ${contentHTML}
        </div>
      `;
    }).join('');
    
    const carouselId = `carousel-${this.id}`;
    
    // Set up navigation handlers
    (window as any)[`__carouselPrev_${carouselId}`] = () => {
      const container = document.getElementById(carouselId);
      if (container) {
        const scrollAmount = container.clientWidth / itemsVisible;
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
    };
    
    (window as any)[`__carouselNext_${carouselId}`] = () => {
      const container = document.getElementById(carouselId);
      if (container) {
        const scrollAmount = container.clientWidth / itemsVisible;
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    };
    
    const prevButton = createButton('←', {
      className: 'carousel-nav-prev',
      variant: 'ghost',
      onClick: `window.__carouselPrev_${carouselId} && window.__carouselPrev_${carouselId}();`
    });
    
    const nextButton = createButton('→', {
      className: 'carousel-nav-next',
      variant: 'ghost',
      onClick: `window.__carouselNext_${carouselId} && window.__carouselNext_${carouselId}();`
    });
    
    return `
      <div style="
        display: flex;
        flex-direction: column;
        height: 100%;
        position: relative;
      ">
        <div style="
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: ${designTokens.padding.sm};
          padding: 0 ${designTokens.padding.sm};
        ">
          ${prevButton}
          <div style="
            font-size: ${designTokens.fontSize.xs};
            color: ${designTokens.contentColorMuted};
          ">${containerNodes.length} items</div>
          ${nextButton}
        </div>
        <div 
          id="${carouselId}"
          style="
            display: flex;
            flex-direction: row;
            overflow-x: auto;
            overflow-y: hidden;
            gap: 0;
            scroll-snap-type: x mandatory;
            flex: 1;
            scrollbar-width: none;
            -ms-overflow-style: none;
          "
          onscroll="this.style.setProperty('--scroll-left', this.scrollLeft + 'px');"
        >
          ${slides}
        </div>
        <style>
          #${carouselId}::-webkit-scrollbar {
            display: none;
          }
        </style>
      </div>
    `;
  }

  private renderGraphView(contentNodes: GraphNode[]): string {
    // Use shared graph renderer
    return renderGraphView(contentNodes, this.graphEngine, {
      width: 700,
      height: 400,
      categoryColors: {
        work: '#66ccff',
        fitness: '#4ade80',
        personal: '#fbbf24',
        health: '#f87171',
        finance: '#a78bfa',
        learning: '#60a5fa',
        default: '#94a3b8'
      }
    });
  }

  private renderStackView(contentNodes: GraphNode[]): string {
    const config = getViewModeConfig('stack', this.state.containerCount);
    const stackId = `stack-${this.id}`;
    const cards = contentNodes.slice(0, this.state.containerCount).map((node, index) => {
      const zIndex = contentNodes.length - index;
      const offset = index * 4;
      const title = this.getNodeData(node, 'title') || 
                    this.getNodeData(node, 'label') || 
                    'Card';
      const description = this.getNodeData(node, 'description', '');
      const imageUrl = this.getNodeData(node, 'image') || 
                       this.getNodeData(node, 'imageUrl') ||
                       this.getNodeData(node, 'thumbnail');
      const hasImage = this.hasNodeData(node, 'image') || 
                       this.hasNodeData(node, 'imageUrl') ||
                       this.hasNodeData(node, 'thumbnail');
      
      return `
        <div 
          class="graph-app-card" 
          data-card-index="${index}"
          data-node-id="${node.id}"
          style="
            position: absolute;
            top: ${offset}px;
            left: ${offset}px;
            right: ${offset}px;
            bottom: ${offset}px;
            z-index: ${zIndex};
            padding: ${designTokens.padding.lg};
            background: ${designTokens.cardBackground};
            border: 1px solid ${designTokens.borderColor};
            border-radius: ${designTokens.radius.md};
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            cursor: grab;
            user-select: none;
            touch-action: none;
            transition: transform 0.3s ease, opacity 0.3s ease;
          "
        >
          ${hasImage && imageUrl ? `
            <div style="
              width: 100%;
              aspect-ratio: 16/9;
              background: rgba(0, 0, 0, 0.1);
              border-radius: ${designTokens.radius.sm};
              overflow: hidden;
              margin-bottom: ${designTokens.padding.md};
            ">
              <img 
                src="${this.escapeHtml(imageUrl)}" 
                alt="${this.escapeHtml(title)}"
                style="width: 100%; height: 100%; object-fit: cover;"
              />
            </div>
          ` : ''}
          <div style="font-weight: 600; font-size: ${designTokens.fontSize.lg}; color: ${designTokens.contentColor};">
            ${this.escapeHtml(title)}
          </div>
          ${description ? `
            <div style="font-size: ${designTokens.fontSize.sm}; color: ${designTokens.contentColorMuted}; margin-top: ${designTokens.padding.md};">
              ${this.escapeHtml(description)}
            </div>
          ` : ''}
        </div>
      `;
    }).join('');
    
    return `
      <div 
        id="${stackId}"
        style="${config.cssLayout}"
        class="graph-app-stack"
        data-stack-id="${stackId}"
      >${cards}</div>
    `;
  }


  /**
   * Setup swipe functionality for card stack
   */
  private setupStackSwipe(stack: HTMLElement): void {
    const interactionMode = this.state.viewOptions?.stackInteractionMode || 'browse';
    const onAction = this.state.viewOptions?.stackOnAction;
    let currentIndex = 0;
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    let currentCard: HTMLElement | null = null;
    const cards = Array.from(stack.querySelectorAll('.graph-app-card')) as HTMLElement[];
    const totalCards = cards.length;
    
    if (totalCards === 0) return;
    
    const updateCardPositions = (): void => {
      cards.forEach((card, index) => {
        if (!card) return;
        const diff = index - currentIndex;
        const offset = Math.abs(diff) * 4;
        const zIndex = totalCards - Math.abs(diff);
        const translateX = diff * 20;
        const opacity = Math.abs(diff) > 2 ? 0.3 : 1 - Math.abs(diff) * 0.2;
        
        card.style.transform = `translateX(${translateX}px) translateY(${offset}px)`;
        card.style.zIndex = String(zIndex);
        card.style.opacity = String(Math.max(0.3, opacity));
      });
    };
    
    const getActionLabel = (direction: 'left' | 'right'): string => {
      if (interactionMode === 'accept-reject') {
        return direction === 'left' ? 'Yes' : 'No';
      } else if (interactionMode === 'complete') {
        return direction === 'left' ? 'Complete' : 'Incomplete';
      }
      return '';
    };
    
    const handleCardAction = (direction: 'left' | 'right'): void => {
      if (!currentCard) return;
      
      const cardIndex = parseInt(currentCard.getAttribute('data-card-index') || '0', 10);
      const nodeId = currentCard.getAttribute('data-node-id') || '';
      
      if (interactionMode === 'browse') {
        // Just navigate to next/previous card
        // Left swipe = previous, Right swipe = next
        if (direction === 'left' && currentIndex > 0) {
          currentIndex--;
        } else if (direction === 'right' && currentIndex < totalCards - 1) {
          currentIndex++;
        }
        updateCardPositions();
      } else {
        // Action-based mode: remove card and move to next
        if (onAction && nodeId) {
          onAction(nodeId, direction);
        }
        
        // Remove the card from view
        currentCard.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
        currentCard.style.transform = `translateX(${direction === 'left' ? '-200%' : '200%'}) translateY(0px)`;
        currentCard.style.opacity = '0';
        
        // Remove card from array after animation
        setTimeout(() => {
          const index = cards.indexOf(currentCard!);
          if (index !== -1) {
            cards.splice(index, 1);
            currentCard?.remove();
          }
          
          // Adjust currentIndex if needed
          if (currentIndex >= cards.length) {
            currentIndex = Math.max(0, cards.length - 1);
          }
          
          updateCardPositions();
          currentCard = null;
        }, 300);
      }
    };
    
    const updateCardDrag = (x: number): void => {
      if (!currentCard || !isDragging) return;
      
      const dragDistance = x - startX;
      const maxDrag = 150;
      const clampedDrag = Math.max(-maxDrag, Math.min(maxDrag, dragDistance));
      const rotation = clampedDrag / 20;
      const opacity = 1 - Math.abs(clampedDrag) / maxDrag * 0.5;
      
      // Get base transform
      const cardIndex = parseInt(currentCard.getAttribute('data-card-index') || '0', 10);
      const diff = cardIndex - currentIndex;
      const offset = Math.abs(diff) * 4;
      const baseTranslateX = diff * 20;
      
      currentCard.style.transform = `translateX(${baseTranslateX + clampedDrag}px) translateY(${offset}px) rotate(${rotation}deg)`;
      currentCard.style.opacity = String(Math.max(0.3, opacity));
      
      // Show action label
      let actionLabel = currentCard.querySelector('.stack-action-label') as HTMLElement;
      if (!actionLabel) {
        actionLabel = document.createElement('div');
        actionLabel.className = 'stack-action-label';
        actionLabel.style.cssText = `
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: ${designTokens.fontSize.lg};
          font-weight: bold;
          color: ${designTokens.contentColor};
          pointer-events: none;
          z-index: 1000;
          opacity: 0;
          transition: opacity 0.2s;
        `;
        currentCard.appendChild(actionLabel);
      }
      
      if (Math.abs(clampedDrag) > 50) {
        // Swipe left (negative drag) = Yes/Accept, Swipe right (positive drag) = No/Reject
        const action = clampedDrag < 0 ? 'left' : 'right';
        actionLabel.textContent = getActionLabel(action);
        actionLabel.style.opacity = String(Math.min(1, Math.abs(clampedDrag) / maxDrag));
      } else {
        actionLabel.style.opacity = '0';
      }
    };
    
    stack.addEventListener('mousedown', (e) => {
      const card = (e.target as HTMLElement).closest('.graph-app-card') as HTMLElement;
      if (card) {
        isDragging = true;
        currentCard = card;
        startX = e.clientX;
        card.style.cursor = 'grabbing';
        card.style.transition = 'none'; // Disable transition during drag
      }
    });
    
    stack.addEventListener('mousemove', (e) => {
      if (!isDragging || !currentCard) return;
      updateCardDrag(e.clientX);
    });
    
    stack.addEventListener('mouseup', () => {
      if (!isDragging || !currentCard) return;
      
      const dragDistance = currentX - startX;
      const threshold = 80;
      
      if (Math.abs(dragDistance) > threshold) {
        // Swipe left (negative) = Yes/Accept, Swipe right (positive) = No/Reject
        const direction = dragDistance < 0 ? 'left' : 'right';
        handleCardAction(direction);
      } else {
        // Snap back
        currentCard.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
        updateCardPositions();
      }
      
      isDragging = false;
      cards.forEach(card => {
        if (card) card.style.cursor = 'grab';
      });
      currentCard = null;
      currentX = 0;
    });
    
    stack.addEventListener('mouseleave', () => {
      if (isDragging && currentCard) {
        currentCard.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
        updateCardPositions();
      }
      isDragging = false;
      cards.forEach(card => {
        if (card) card.style.cursor = 'grab';
      });
      currentCard = null;
      currentX = 0;
    });
    
    // Touch events
    stack.addEventListener('touchstart', (e) => {
      const card = (e.target as HTMLElement).closest('.graph-app-card') as HTMLElement;
      if (card) {
        isDragging = true;
        currentCard = card;
        startX = e.touches[0].clientX;
        card.style.transition = 'none';
      }
    });
    
    stack.addEventListener('touchmove', (e) => {
      if (!isDragging || !currentCard) return;
      e.preventDefault();
      currentX = e.touches[0].clientX;
      updateCardDrag(currentX);
    });
    
    stack.addEventListener('touchend', () => {
      if (!isDragging || !currentCard) return;
      
      const dragDistance = currentX - startX;
      const threshold = 80;
      
      if (Math.abs(dragDistance) > threshold) {
        // Swipe left (negative) = Yes/Accept, Swipe right (positive) = No/Reject
        const direction = dragDistance < 0 ? 'left' : 'right';
        handleCardAction(direction);
      } else {
        currentCard.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
        updateCardPositions();
      }
      
      isDragging = false;
      currentCard = null;
      currentX = 0;
    });
    
    updateCardPositions();
  }

  updateState(newState: GraphAppState): void {
    // Add smooth transition effect
    const visualization = this.element?.querySelector('.graph-app-visualization');
    if (visualization) {
      (visualization as HTMLElement).style.opacity = '0.5';
      (visualization as HTMLElement).style.transform = 'scale(0.98)';
    }
    
    this.state = newState;
    this.process['rebuildGraph']();
    
    // Trigger re-render with animation
    setTimeout(() => {
      this.markDirty();
      if (visualization) {
        (visualization as HTMLElement).style.opacity = '1';
        (visualization as HTMLElement).style.transform = 'scale(1)';
      }
    }, 50);
  }
}

registerProcess('graph-app', (id: string) => new GraphAppProcess(id));

